// src/utils/recoverableDepreciation.js
//
// Depreciación recuperable (Recoverable Depreciation, RD) — pedido del
// cliente (2026-10-21). El estimado del seguro suele traer un monto de RD
// que se puede pedir cuando el reclamo ya se pagó y el cliente reparó:
// se envían las pruebas de pago/reparación y el seguro emite otro cheque.
//
// El monto (`recoverableDepreciation`) y la fecha en que se pidió
// (`rdRequestedDate`) viven en el reclamo. El estado se deduce:
//   none            → el reclamo no tiene RD cargada.
//   waiting_payment → hay RD, pero el cliente todavía no cobró ningún cheque.
//   to_request      → el cliente ya cobró: hay que pedir la RD (alerta).
//   requested       → se pidió; se espera el cheque de RD.
//   received        → llegó un cheque con tipo "Recoverable Depreciation".
// "El cliente cobró" = algún cheque del reclamo pasó por "Desembolso al
// cliente" (está en el tramo B de CHECK_FLOW: valores reales, pago de
// comisión o comisión cobrada).
import { CHECK_FLOW } from '../config/claimFlow';
import { proposeCheckStep } from '../migration/flowMigration';

export const RD_PAYMENT_TYPE = 'Recoverable Depreciation';

const PAID_TO_CLIENT_STEPS = CHECK_FLOW.steps.filter(step => step.phase === 'B').map(step => step.id);

export const rdAmount = claimRecord => {
  const value = claimRecord?.recoverableDepreciation;
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

export const isRdCheck = check => check?.paymentType === RD_PAYMENT_TYPE;

export const isPaidToClient = check => {
  const stepId = check?.flow?.stepId || proposeCheckStep(check || {});
  return PAID_TO_CLIENT_STEPS.includes(stepId);
};

// `checks`: los cheques de ESTE reclamo.
export const rdStatus = (claimRecord, checks = []) => {
  if (rdAmount(claimRecord) === null) return 'none';
  if (checks.some(isRdCheck)) return 'received';
  if (claimRecord.rdRequestedDate) return 'requested';
  return checks.some(check => !isRdCheck(check) && isPaidToClient(check)) ? 'to_request' : 'waiting_payment';
};

export const RD_STATUS_LABELS = {
  none: ['Sin depreciación recuperable', 'No recoverable depreciation'],
  waiting_payment: ['Se pide cuando el cliente cobre', 'Request it once the client is paid'],
  to_request: ['Lista para pedir al seguro', 'Ready to request from the insurance'],
  requested: ['Pedida · esperando el cheque', 'Requested · waiting for the check'],
  received: ['Cheque de RD recibido', 'RD check received']
};

// Reclamos con RD lista para pedir (alerta de la pantalla Claims).
export const rdAlerts = (claimRecords = [], checks = []) => {
  const byClaim = new Map();
  checks.forEach(check => {
    if (!check.claimRecordId) return;
    if (!byClaim.has(check.claimRecordId)) byClaim.set(check.claimRecordId, []);
    byClaim.get(check.claimRecordId).push(check);
  });
  return claimRecords
    .filter(cr => rdStatus(cr, byClaim.get(cr.id) || []) === 'to_request')
    .map(cr => ({ claimRecordId: cr.id, clientName: cr.clientName, claimNumber: cr.claimNumber, adjusterName: cr.adjusterName, amount: rdAmount(cr) }));
};
